import React from "react";
import { FaPlus, FaTrash } from "react-icons/fa"; // Import the plus icon from react-icons

const FamilyMemberRow = ({
    member,
    index,
    handleChange,
    handleRemove,
    fileUpload,
    nextLoader,
    DOBPickerComponent, // passed as prop
}) => {
    return (
        <div className="flex items-end gap-2 mb-4 w-full overflow-x-auto scrollbar-hide md:gap-3 lg:gap-4 print:overflow-visible">
            {/* Name */}
            <div className="flex-1 min-w-[120px] md:min-w-[140px] lg:min-w-[160px]">
                {fileUpload ? (
                    <div className="px-2 py-2 min-h-[2.5rem] w-full bg-slate-50 border-b-2 border-b-gray-300 font-medium rounded-md text-black text-sm">
                        {member.name}
                    </div>
                ) : (
                    <textarea
                        value={member.name}
                        onChange={(e) => handleChange(index, "name", e.target.value)}
                        className="w-full px-2 py-2 bg-slate-50 border-b-2 border-b-gray-300 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm min-h-[2.5rem]"
                        rows="1"
                        placeholder="Name"
                    />
                )}
            </div>

            {/* Relationship */}
            <div className="flex-1 min-w-[100px] md:min-w-[120px] lg:min-w-[140px]">
                {fileUpload ? (
                    <div className="px-2 py-2 min-h-[2.5rem] w-full bg-slate-50 border-b-2 border-b-gray-300 font-medium rounded-md text-black text-sm">
                        {member.relationship}
                    </div>
                ) : (
                    <textarea
                        value={member.relationship}
                        onChange={(e) => handleChange(index, "relationship", e.target.value)}
                        className="w-full px-2 py-2 bg-slate-50 border-b-2 border-b-gray-300 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm min-h-[2.5rem]"
                        rows="1"
                        placeholder="Relationship"
                    />
                )}
            </div>

            {/* DOB */}
            <div className="flex-1 min-w-[100px] md:min-w-[120px] lg:min-w-[140px]">
                {fileUpload ? (
                    <div className="px-2 py-2 min-h-[2.5rem] w-full bg-slate-50 border-b-2 border-b-gray-300 font-medium rounded-md text-black text-sm">
                        {member.dob}
                    </div>
                ) : DOBPickerComponent ? (
                    <DOBPickerComponent
                        id="dob"
                        value={member.dob}
                        onChange={(date) => {
                            const formattedDate = date
                                ? `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
                                    .getDate()
                                    .toString()
                                    .padStart(2, "0")}/${date.getFullYear()}`
                                : "";
                            handleChange(index, "dob", formattedDate);
                        }}
                        disabled={nextLoader}
                    />
                ) : (
                    <input
                        type="text"
                        value={member.dob}
                        onChange={(e) => handleChange(index, "dob", e.target.value)}
                        placeholder="mm/dd/yyyy"
                        className="w-full px-2 py-2 bg-slate-50 border-b-2 border-b-gray-300 font-medium rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                )}
            </div>

            {/* Phone */}
            <div className="flex-1 min-w-[120px] md:min-w-[140px] lg:min-w-[160px]">
                {fileUpload ? (
                    <div className="px-2 py-2 min-h-[2.5rem] w-full bg-slate-50 border-b-2 border-b-gray-300 font-medium rounded-md text-black text-sm">
                        {member.mobile_number}
                    </div>
                ) : (
                    <textarea
                        value={member.mobile_number}
                        onChange={(e) => handleChange(index, "mobile_number", e.target.value)}
                        className="w-full px-2 py-2 bg-slate-50 border-b-2 border-b-gray-300 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm min-h-[2.5rem]"
                        rows="1"
                        placeholder="Phone"
                    />
                )}
            </div>

            {/* Delete Button */}
            {!fileUpload && (
                <div className="flex items-end min-w-[50px] md:min-w-[60px] pb-1 print:hidden">
                    <button
                        type="button"
                        onClick={() => handleRemove(index)}
                        className="text-red-500 hover:text-red-700 p-2 md:p-3 rounded-md hover:bg-red-50 transition-colors duration-200"
                        aria-label="Delete family member"
                    >
                        <FaTrash className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                </div>
            )}
        </div>
    );
};

const FamilyMemberConsent = ({
    familyMembers,
    handleFamilyChange,
    handleRemoveFamilyMember,
    handleAddMember,
    fileUpload = false,
    nextLoader = false,
    DOBPickerComponent, // pass your custom date picker here
    title
}) => {
    return (
        <div className="px-1 py-2 bg-white">
            <div>
                <p className="border-t-4 border-b-4 border-gray-300 p-4 mt-4 text-lg font-semibold text-center text-black">Family Member Consent</p>
            </div>

            <div className="text-black mt-2">
                <p>
                    With your permission, {title} may release your protected health information to a family member, or another person involved in your care or payment for your care.
                </p>
                <p>
                    Please identify the person or persons who are involved in your care or the payment of your care that you can authorize to receive your protected health information. This may include your spouse, parent, siblings, children, close friend, or guardian. Please list below:
                </p>
            </div>

            <div className="mt-4">
                {/* Table Header */}
                <div className="flex gap-2 mb-2 font-medium text-sm text-black print:hidden md:gap-3 lg:gap-4">
                    <div className="flex-1 min-w-[120px] md:min-w-[140px] lg:min-w-[160px]">
                        Name <span className="text-red-500">*</span>
                    </div>

                    <div className="flex-1 min-w-[100px] md:min-w-[120px] lg:min-w-[140px]">
                        Relationship <span className="text-red-500">*</span>
                    </div>

                    <div className="flex-1 min-w-[100px] md:min-w-[120px] lg:min-w-[140px]">
                        DOB <span className="text-red-500">*</span>
                    </div>

                    <div className="flex-1 min-w-[120px] md:min-w-[140px] lg:min-w-[160px]">
                        Phone <span className="text-red-500">*</span>
                    </div>
                    <div className="min-w-[50px] md:min-w-[60px]" />
                </div>

                {/* Family Rows */}
                {familyMembers.map((member, index) => (
                    <FamilyMemberRow
                        key={index}
                        member={member}
                        index={index}
                        handleChange={handleFamilyChange}
                        handleRemove={handleRemoveFamilyMember}
                        fileUpload={fileUpload}
                        nextLoader={nextLoader}
                        DOBPickerComponent={DOBPickerComponent}
                    />
                ))}

                {/* Add Member Button */}
                {!fileUpload && (
                    <div className="flex justify-start mt-4">
                        <button
                            type="button"
                            onClick={handleAddMember}
                            className="flex items-center text-blue-500 hover:text-blue-700"
                        >
                            <FaPlus className="mr-2" /> Add Family Member
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FamilyMemberConsent;
